/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import * as R from 'ramda';
import * as React from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import waiverImg from '../../../assets/icons/waiver.svg';
import { ACTION_TYPE } from '../../../core/redux/actionType';
import { getError, getLoading } from '../../../core/redux/api/module';
import { IAppState } from '../../../core/redux/IAppState';
import { isNilOrEmpty, assoc } from '../../../language/module';
import { MODAL_SIZE } from '../../../layout/modal/modalSize';
import ModalTitleHeader from '../../../layout/modal/ModalTitleHeader';
import ModalView, { ModalContent } from '../../../layout/modal/ModalView';
import { GuestDetails } from '../../guestDetails';
import { fullName } from '../../module';
import { getActiveEntitlementTypes } from '../module';
import { getRequiredWaivers, isSignatureEnabled } from './module';
import { submitWaiverAction } from './submitWaiverAction';
import { Waiver } from './waiver';
import { WaiverSignature } from './waiverSignature';
import WaiverSignatureForm from './WaiverSignatureForm';
import { WAIVER_STATUS } from './waiverStatus';
import WaiverTable from './WaiverTable';
import { waiverLookupAction } from './waiverLookupAction';
import { EntitlementType } from '../../../entitlementTypes/entitlementType';
import ImmutableMap from '../../../language/ImmutableMap';
import { getResource } from '../../../language/resources/getResource';


type Props = {
  guestId: string;
  guest?: GuestDetails;
  onDismiss: () => void;
  submitWaiver?: (guest: GuestDetails, signature: WaiverSignature) => void,
  isLoading?: boolean;
  rootCss?: SerializedStyles;
  allEntitlementTypes?: ImmutableMap<EntitlementType>;
  waiverLookup: ImmutableMap<string>;
  populateWaiverLookup?: () => void;
  isWaiverLookupLoading?: boolean;
  waiverLookupError?: boolean;
  resources: Record<string, any>;
  dateFormat: string;
};

type State = {
  waiverToSign: Waiver | undefined;
}

/**
 * WaiverModal: a component that shows a table of the user's waivers along with signature status and a sign button when
 * applicable. When the sign button is clicked, slides the table out and the signature form in for the guest to sign the
 * waiver right there.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState, props: Props) => ({
    guest:                 state.guests.get(props.guestId),
    isLoading:             !!getLoading(state, props.guestId, ACTION_TYPE.SUBMIT_WAIVER_REQUEST),
    allEntitlementTypes:   state.entitlementTypesDetails?.allEntitlementTypes,
    waiverLookup:          state.waiverLookup,
    isWaiverLookupLoading: !!getLoading(state, ACTION_TYPE.GET_WAIVER_LOOKUP_REQUEST, ACTION_TYPE.GET_WAIVER_LOOKUP_REQUEST),
    waiverLookupError:     !!getError(state, ACTION_TYPE.GET_WAIVER_LOOKUP_REQUEST, ACTION_TYPE.GET_WAIVER_LOOKUP_REQUEST),
    resources:             state.locale,
    dateFormat:            state.defaultDateFormat,
  }),
  dispatch => ({
    submitWaiver: (guest: GuestDetails, signature: WaiverSignature) => dispatch(submitWaiverAction(guest, signature)),
    populateWaiverLookup: () => dispatch(waiverLookupAction()),
  }),
)(class WaiverModal extends React.PureComponent<Props, State> {
  public state: State = {
    waiverToSign: undefined,
  };

  public componentDidMount() {
    if(this.props.waiverLookup.isEmpty() && !this.props.isWaiverLookupLoading && !this.props.waiverLookupError){
      this.props.populateWaiverLookup!();
    }
  }


  public toWaiverModel = (version: string, title: string, guest: GuestDetails): Waiver => {
    const waiver = R.find(guestWaiver => guestWaiver.version === version, guest.waivers);
    const activeEntitlements = getActiveEntitlementTypes(guest, this.props.allEntitlementTypes);
    const enableSignature = isSignatureEnabled(activeEntitlements, +version);

    return !isNilOrEmpty(waiver)
      ? ({...waiver, enableSignature, title} as Waiver)
      : ({
        waiverType:            +version, // Deprecated
        title:                 title,
        version:               version,
        waiverComments:        '',
        signedByMasterGuestId: '',
        signedDate:            '',
        expirationDate:        '',
        status:                WAIVER_STATUS.UNSIGNED,
        enableSignature:       enableSignature,
      });
  };


  public makeWaiverData = (guest: GuestDetails) => {
    const requiredVersions = getRequiredWaivers(
      getActiveEntitlementTypes(guest, this.props.allEntitlementTypes),
    );

    const allLinkedWaivers = (requiredVersions || []).map(
      version => {
        let description;

        if (this.props.isWaiverLookupLoading) {
          description = getResource(this.props.resources, 'WAIVER_MODAL_LOADING');
        }

        else if (this.props.waiverLookupError) {
          description = getResource(this.props.resources, 'WAIVER_MODAL_ERROR');
        }

        else {
          description = this.props.waiverLookup.get(`${version}`) || getResource(this.props.resources, 'WAIVER_MODAL_UNKNOWN_TYPE', version);
        }

        return this.toWaiverModel(`${version}`, description, guest)
      },
    );

    return allLinkedWaivers.filter(it => it.enableSignature || it.status === WAIVER_STATUS.SIGNED);
  };


  private onSignClick = (waiver: Waiver): void => this.setState(prev => assoc('waiverToSign', waiver, prev));


  private onCancelSign = (): void => this.setState(prev => assoc('waiverToSign', undefined, prev));


  private onSubmitSignature = (signature: WaiverSignature): void => {
    this.setState(prev => assoc('waiverToSign', undefined, prev));
    this.props.submitWaiver!(this.props.guest!, signature);
  };

  private nodeRef = React.createRef<HTMLDivElement>();


  public render = () => {
    const hasWaiverToSign = !isNilOrEmpty(this.state.waiverToSign);

    return (
      <ModalView
        onDismiss={this.props.onDismiss}
        rootCss={this.props.rootCss}
        size={MODAL_SIZE.MEDIUM}
        contentCss={css`
          position: relative;
          min-height: 390px; /* Height of waiver form */
        `}
      >
        <ModalTitleHeader resources={this.props.resources} heading={getResource(this.props.resources, 'WAIVER_MODAL_HEADING')} icon={waiverImg} onDismiss={this.props.onDismiss} />
        <ModalContent css={css`min-height: 390px; position: relative;`}>
          {/* @ts-ignore*/}
          <TransitionGroup component={null} nodeRef={this.nodeRef}>
            {
              hasWaiverToSign
                ? ( // Waiver form for when guest clicks pencil to sign waiver
                  /* @ts-ignore*/
                  <CSSTransition
                    key='waiverForm'
                    appear={true}
                    unmountOnExit={true}
                    mountOnEnter={true}
                    classNames='slide-in-right'
                    timeout={500}
                    nodeRef={this.nodeRef}
                  >
                    <WaiverSignatureForm
                      resources={this.props.resources}
                      version={this.state.waiverToSign!.version}
                      onCancel={this.onCancelSign}
                      onSubmit={this.onSubmitSignature}
                      rootCss={css`width: 744px;`}
                    />
                  </CSSTransition>
                )
                : ( // Table of available waivers to sign/view
                   /* @ts-ignore*/
                  <CSSTransition key='waiverTable' classNames='slide-in-left' appear={true} timeout={500} nodeRef={this.nodeRef}>
                    <WaiverTable
                      resources={this.props.resources}
                      dateFormat={this.props.dateFormat}
                      guestName={fullName(this.props.guest)}
                      isLoading={this.props.isLoading!}
                      data={this.makeWaiverData(this.props.guest!)}
                      onSignClick={this.onSignClick}
                      rootCss={css`width: 744px;`}
                    />
                  </CSSTransition>
                )
            }
          </TransitionGroup>
        </ModalContent>
      </ModalView>
    );
  };
});
